import { MouseEventHandler, ReactNode } from 'react';
import classNames from 'classnames';

import { Icon, IconName, IconProps } from '../icon';
import { FormInputSize } from '../form/types';
import { Loader } from '../loader';
import { Dropdown, DropdownMenuItemActionable } from '../dropdown';
import { Tooltip } from '../tooltip';

import styles from './button.module.scss';

type InlineButtonProps = {
  ref?: React.Ref<HTMLButtonElement>;
  children: string | React.ReactNode;
  onClick?: MouseEventHandler;
  title?: string;
  style?: React.CSSProperties;
  className?: classNames.Argument;
  disabled?: boolean;
  color?: 'white' | 'green';
  size?: FormInputSize;
  'aria-labelledby'?: string;
};

const getIconSize = (size?: FormInputSize) => (size === 'small' ? 12 : size === 'large' ? 24 : 16);

export function InlineButton({
  ref,
  children,
  onClick,
  className,
  disabled,
  color,
  style,
  title,
  size = 'regular',
  'aria-labelledby': ariaLabelledby,
}: InlineButtonProps) {
  return (
    <button
      ref={ref}
      style={style}
      className={classNames(styles.inlineTextBtn, className, {
        [styles.white]: color === 'white',
        [styles.green]: color === 'green',
        [styles.sizeSmall]: size === 'small',
        [styles.sizeMedium]: size === 'regular',
      })}
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      aria-labelledby={ariaLabelledby}>
      {children}
    </button>
  );
}

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  ref?: React.Ref<HTMLButtonElement>;
  loading?: boolean;
  icon?: React.ReactNode;
  variant?: 'primary' | 'secondary' | 'outlined' | 'gray';
  color?: 'default' | 'warning';
  size?: FormInputSize | 'compact';
  'aria-labelledby'?: string;
};

export function Button({
  ref,
  children,
  icon,
  className,
  disabled = false,
  loading = false,
  variant = 'primary',
  size = 'regular',
  color = 'default',
  'aria-labelledby': ariaLabelledby,
  ...rest
}: ButtonProps) {
  return (
    <button
      ref={ref}
      {...rest}
      className={classNames(styles.button, className, {
        [styles.primary]: variant === 'primary',
        [styles.secondary]: variant === 'secondary',
        [styles.outlined]: variant === 'outlined',
        [styles.styleGray]: variant === 'gray',
        [styles.sizeSmall]: size === 'small',
        [styles.sizeCompact]: size === 'compact',
        [styles.sizeMedium]: size === 'regular',
        [styles.sizeLarge]: size === 'large',
        [styles.warning]: color === 'warning',
        [styles.loading]: loading,
      })}
      disabled={disabled || loading}
      aria-label={rest.title}
      aria-labelledby={ariaLabelledby}>
      {icon}
      <span>{children}</span>
      {loading && <Loader size="small" className={styles.loader} />}
    </button>
  );
}

type IconButtonProps = {
  ref?: React.Ref<HTMLButtonElement>;
  icon: IconProps['name'];
  title?: string;
  iconSize?: FormInputSize;
  type?: 'regular' | 'outlined' | 'gray';
  onClick?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  disabled?: boolean;
  className?: classNames.Argument;
  style?: React.CSSProperties;
  size?: FormInputSize;
  tabIndex?: number;
  'aria-labelledby'?: string;
};

export function IconButton({
  ref,
  icon,
  size = 'small',
  iconSize,
  type,
  onClick,
  onMouseLeave,
  disabled,
  className,
  style,
  title,
  tabIndex,
  'aria-labelledby': ariaLabelledby,
}: IconButtonProps) {
  return (
    <button
      ref={ref}
      style={style}
      className={classNames(styles.iconButton, className, {
        [styles.styleOutlined]: type === 'outlined',
        [styles.styleGray]: type === 'gray',
        [styles.sizeSmall]: size === 'small',
        [styles.sizeMedium]: size === 'regular',
        [styles.sizeLarge]: size === 'large',
      })}
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      onMouseLeave={onMouseLeave}
      disabled={disabled}
      tabIndex={tabIndex}
      aria-labelledby={ariaLabelledby}>
      <Icon name={icon} size={getIconSize(iconSize)} />
    </button>
  );
}

type CloseButtonProps = {
  ref?: React.Ref<HTMLButtonElement>;
  iconSize?: FormInputSize;
  className?: classNames.Argument;
  onClick?: MouseEventHandler;
  tabIndex?: number;
  title?: string;
  'aria-labelledby'?: string;
};

export function CloseButton({
  ref,
  iconSize,
  className,
  title = 'Close',
  onClick,
  tabIndex,
  'aria-labelledby': ariaLabelledby,
}: CloseButtonProps) {
  return (
    <IconButton
      ref={ref}
      icon="X"
      title={title}
      aria-label="Close"
      iconSize={iconSize}
      className={className}
      onClick={onClick}
      tabIndex={tabIndex}
      aria-labelledby={ariaLabelledby}
    />
  );
}

type ScrollToButtonProps = {
  ref?: React.Ref<HTMLButtonElement>;
  label: string;
  direction: 'up' | 'down';
  className?: classNames.Argument;
  onClick: MouseEventHandler;
};

export function ScrollToButton({ ref, label, direction, className, onClick }: ScrollToButtonProps) {
  return (
    <button
      ref={ref}
      className={classNames(styles.scrollToButton, className)}
      type="button"
      onClick={onClick}>
      <Icon name={direction === 'up' ? 'ArrowUp' : 'ArrowDown'} size={16} />
      {label}
    </button>
  );
}

interface DropDownButtonProps {
  ref?: React.Ref<HTMLButtonElement>;
  children: string;
  icon?: IconName;
  size?: FormInputSize;
  variant?: 'primary' | 'secondary' | 'outlined' | 'gray';
  iconSize?: number;
  className?: classNames.Argument;
  'aria-labelledby'?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

export function DropDownButton({
  ref,
  size = 'small',
  variant,
  icon,
  children,
  className,
  'aria-labelledby': ariaLabelledby,
  onClick,
}: DropDownButtonProps) {
  const iconSize = getIconSize(size);
  const chevron = <Icon name="ChevronDownThin" size={size === 'small' ? 14 : 16} />;

  return (
    <button
      ref={ref}
      className={classNames(styles.dropDownButton, className, {
        [styles.sizeSmall]: size === 'small',
        [styles.sizeMedium]: size === 'regular',
        [styles.sizeLarge]: size === 'large',
        [styles.primary]: variant === 'primary',
        [styles.secondary]: variant === 'secondary',
        [styles.outlined]: variant === 'outlined',
        [styles.styleGray]: variant === 'gray',
      })}
      type="button"
      onClick={onClick}
      aria-labelledby={ariaLabelledby}>
      {icon !== undefined ? <Icon name={icon} size={iconSize} /> : ''}
      <span>{children}</span>
      {chevron}
    </button>
  );
}

interface SplitButtonProps {
  ref?: React.Ref<HTMLButtonElement>;
  size?: FormInputSize;
  variant?: 'primary' | 'secondary' | 'outlined' | 'gray';
  iconSize?: number;
  className?: classNames.Argument;
  actions: DropdownMenuItemActionable[];
  'aria-labelledby'?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

export function SplitButton({
  ref,
  variant = 'primary',
  size = 'regular',
  actions,
  className,
  'aria-labelledby': ariaLabelledby,
  onClick,
}: SplitButtonProps) {
  const [primaryAction, ...actionsList] = actions;

  return (
    <div
      className={classNames(styles.splitWrapper, className, {
        [styles.primary]: variant === 'primary',
        [styles.secondary]: variant === 'secondary',
        [styles.outlined]: variant === 'outlined',
        [styles.styleGray]: variant === 'gray',
        [styles.sizeSmall]: size === 'small',
        [styles.sizeMedium]: size === 'regular',
        [styles.sizeLarge]: size === 'large',
      })}>
      <Button
        ref={ref}
        variant={variant}
        size={size}
        icon={primaryAction.icon}
        onClick={onClick}
        aria-labelledby={ariaLabelledby}>
        {primaryAction.label}
      </Button>
      <Dropdown
        align="right"
        trigger={(isOpen, setIsOpen) => (
          <button
            type="button"
            className={classNames(styles.splitButton, className, {
              [styles.primary]: variant === 'primary',
              [styles.secondary]: variant === 'secondary',
              [styles.outlined]: variant === 'outlined',
              [styles.styleGray]: variant === 'gray',
              [styles.sizeSmall]: size === 'small',
              [styles.sizeMedium]: size === 'regular',
              [styles.sizeLarge]: size === 'large',
            })}
            onClick={() => setIsOpen(!isOpen)}>
            <Icon name="ChevronDownThin" size={16} />
          </button>
        )}
        items={actionsList}
      />
    </div>
  );
}

type ButtonGroupProps = {
  ref?: React.Ref<HTMLDivElement>;
  options: { label: ReactNode; value: string; title?: string }[];
  value: string;
  size: FormInputSize;
  onChange: (value: string) => void;
  style?: React.CSSProperties;
  'aria-labelledby'?: string;
};

export function ButtonGroup({
  ref,
  options,
  value,
  size = 'regular',
  onChange,
  style,
  'aria-labelledby': ariaLabelledby,
}: ButtonGroupProps) {
  return (
    <div
      ref={ref}
      className={classNames(styles.buttonGroup, {
        [styles.sizeSmall]: size === 'small',
        [styles.sizeMedium]: size === 'regular',
        [styles.sizeLarge]: size === 'large',
      })}
      style={style}>
      {options.map((option) => (
        <Tooltip key={option.value} content={option.title}>
          <button
            type="button"
            className={classNames({
              [styles.sizeSmall]: size === 'small',
              [styles.sizeMedium]: size === 'regular',
              [styles.sizeLarge]: size === 'large',
              [styles.active]: option.value === value,
            })}
            onClick={() => onChange(option.value)}
            aria-labelledby={ariaLabelledby}>
            {option.label}
          </button>
        </Tooltip>
      ))}
    </div>
  );
}
